import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import { colors } from 'styles/theme';
import { mobileOnly, tabletUp } from 'styles/utils';

import { imageShape } from 'components/InfoCenter/InfoCenter.config';

import Text from '../Text/Text';

import PromoBase from './PromoBase';
import PromoActionArea from './PromoActionArea';
import PromoMedia from './PromoMedia';
import PromoContent from './PromoContent';
import VideoPlayIcon from './VideoPlayIcon';

const promoBaseCss = cardOnly =>
  css`
    &.MuiCard-root {
      ${mobileOnly} {
        ${!cardOnly &&
        css`
          box-shadow: none;
        `}
      }
    }
  `;

const promoActionAreaCss = cardOnly =>
  css`
    &.MuiCardActionArea-root {
      ${mobileOnly} {
        ${!cardOnly &&
        css`
          display: flex;
          justify-content: flex-start;
          align-items: flex-start;
        `}
      }
    }
  `;

const promoMediaCss = cardOnly =>
  css`
    &.MuiCardMedia-root {
      ${mobileOnly} {
        ${!cardOnly &&
        css`
          height: 69px;
          max-width: 91px;
          min-width: 91px;
          border-radius: 5px;
          margin-top: 4px;
          padding-top: initial;
        `}
      }
    }
  `;

const promoContentCss = cardOnly => css`
  &.MuiCardContent-root {
    justify-content: ${cardOnly && 'space-between'};

    ${mobileOnly} {
      ${!cardOnly &&
      css`
        padding: 0 14px;
        min-height: 73px;
      `}

      flex-direction: ${cardOnly && 'column-reverse'};
    }

    ${tabletUp} {
      justify-content: space-between;
      flex-direction: column-reverse;
    }
  }
`;

/**
 * A card element that presents an Info Center post in a promotional manner.
 *
 * @param {object} props
 */
function PromoCard({
  cardOnly,
  isVideo,
  length,
  lengthDescribed,
  linkAs,
  linkHref,
  promoTitle,
  thumbnail,
  title,
}) {
  return (
    <PromoBase css={promoBaseCss(cardOnly)} linkHref={linkHref} linkAs={linkAs}>
      <PromoActionArea css={promoActionAreaCss(cardOnly)}>
        {/* Thumbnail */}
        <PromoMedia css={promoMediaCss(cardOnly)} thumbnailUrl={thumbnail.url}>
          {isVideo && <VideoPlayIcon cardOnly={cardOnly} />}
        </PromoMedia>
        <PromoContent css={promoContentCss(cardOnly)}>
          {/* Title */}
          <Text component="h4" fontWeight={400} truncate={2}>
            {promoTitle ?? title}
          </Text>
          {/* Length */}
          {length && (
            <Text variant="caption" fontWeight={400} color={colors.brownGray}>
              {isVideo ? length : lengthDescribed}
            </Text>
          )}
        </PromoContent>
      </PromoActionArea>
    </PromoBase>
  );
}

PromoCard.propTypes = {
  /** Show promo as a card on all screen sizes */
  cardOnly: PropTypes.bool,
  isVideo: PropTypes.bool,
  length: PropTypes.string,
  lengthDescribed: PropTypes.string,
  linkAs: PropTypes.string,
  linkHref: PropTypes.string,
  promoTitle: PropTypes.string,
  thumbnail: PropTypes.shape(imageShape),
  title: PropTypes.string,
};

export default PromoCard;
